import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
// Hooks
import { useMedicationEvents } from './hooks/useMedicationEvents';
import { useCalendarStyles } from './hooks/useCalendarStyles';
import { useScheduleHandlers } from './hooks/useScheduleHandlers';
// Components
import {
  EventComponent,
  DayHeader,
  TimeHeaderGutter,
} from './components/CalendarComponents';
import { CustomTopbar } from './components/CustomTopbar';
import ScheduleEventPopup from './components/ScheduleEventPopup';
import SlotOverflowPopover from './components/SlotOverflowPopover';
import EditScheduleModal from './components/EditScheduleModal';
import { buildCalendarEvents, SLOT_MINUTES } from './utils/slotLayout';
import { ScheduleCalendarEvent } from './types';
import 'moment-timezone';
import LoadingSpinner from '../../../../Shared/LoadingSpinner';

const localizer = momentLocalizer(moment);

interface ScheduleTabProps {
  ownerId: number;
  ownerName?: string;
  switchToMedicationTab?: () => void;
}

const ScheduleTab: React.FC<ScheduleTabProps> = ({
  ownerId,
  ownerName,
  switchToMedicationTab,
}) => {
  // Get calendar state and handlers
  const {
    selectedEvent,
    popupAnchor,
    editModalOpen,
    currentDate,
    currentView,
    overflowEvents,
    overflowAnchor,
    handleSelectEvent,
    handleSelectOverflow,
    handleSelectOverflowEvent,
    handleOverflowClose,
    handlePopupClose,
    handleEdit,
    handleEditModalClose,
    setCurrentDate,
    setCurrentView,
  } = useScheduleHandlers();

  const weekStartDate = useMemo(
    () => moment(currentDate).startOf('week'),
    [currentDate],
  );
  const weekEndDate = useMemo(
    () => moment(currentDate).endOf('week').add(1, 'day'),
    [currentDate],
  );

  const { events, isLoading } = useMedicationEvents(
    ownerId,
    weekStartDate,
    weekEndDate,
    ownerName,
    false, // exclude invalidated events in calendar view
  );

  // Bucket events into 15-minute slots, collapsing dense slots into a "+N" block
  const calendarEvents = useMemo(() => buildCalendarEvents(events), [events]);

  const earliestEventTime = useMemo(() => {
    if (!events.length) return new Date(1970, 1, 1, 8, 0, 0); // fallback to 8 AM
    const minutesIntoDay = events.map((e) => {
      const d = new Date(e.start);
      return isNaN(d.getTime()) ? 8 * 60 : d.getHours() * 60 + d.getMinutes();
    });
    const earliest = Math.min(...minutesIntoDay);
    return new Date(1970, 1, 1, Math.floor(earliest / 60), 0, 0);
  }, [events]);

  // Real events open the detail popup; the "+N" block opens the slot list
  const handleCalendarSelect = useCallback(
    (calendarEvent: ScheduleCalendarEvent, e: React.SyntheticEvent) => {
      if (calendarEvent.medEvent) {
        handleSelectEvent(calendarEvent.medEvent, e);
        return;
      }
      handleSelectOverflow(calendarEvent.overflowEvents, e);
    },
    [handleSelectEvent, handleSelectOverflow],
  );

  // rbc gives each event role="button" and tabIndex 0 but forwards every keydown
  // unfiltered, so only activation keys may open a popup
  const handleCalendarKeyPress = useCallback(
    (calendarEvent: ScheduleCalendarEvent, e: React.SyntheticEvent) => {
      const { key } = e as React.KeyboardEvent<HTMLElement>;
      if (key !== 'Enter' && key !== ' ') return;
      e.preventDefault();
      handleCalendarSelect(calendarEvent, e);
    },
    [handleCalendarSelect],
  );

  // Get calendar styles and formats
  const { calendarStyle, formats } = useCalendarStyles();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {/* Calendar view */}
      <Box sx={calendarStyle}>
        <Calendar<ScheduleCalendarEvent>
          localizer={localizer}
          defaultView="week"
          startAccessor={(event) => event.slotStart}
          endAccessor={(event) => event.slotEnd}
          titleAccessor={(event) => event.medEvent?.title ?? ''}
          events={calendarEvents}
          onSelectEvent={handleCalendarSelect}
          onKeyPressEvent={handleCalendarKeyPress}
          formats={formats}
          view={currentView}
          onView={setCurrentView}
          date={currentDate}
          onNavigate={setCurrentDate}
          components={{
            event: EventComponent,
            toolbar: CustomTopbar,
            header: DayHeader,
            timeGutterHeader: TimeHeaderGutter,
          }}
          step={SLOT_MINUTES}
          timeslots={1}
          dayLayoutAlgorithm="no-overlap"
          scrollToTime={earliestEventTime}
        />
      </Box>

      {/* Overflow list for dense slots */}
      <SlotOverflowPopover
        events={overflowEvents}
        anchorEl={overflowAnchor}
        open={Boolean(overflowEvents.length && overflowAnchor)}
        onClose={handleOverflowClose}
        onSelectEvent={handleSelectOverflowEvent}
      />

      {/* Event Popup */}
      <ScheduleEventPopup
        event={selectedEvent}
        anchorEl={popupAnchor}
        open={Boolean(selectedEvent && popupAnchor)}
        onClose={handlePopupClose}
        onEdit={handleEdit}
        switchToMedicationTab={switchToMedicationTab}
        customerId={ownerId}
      />

      {/* Edit Modal */}
      <EditScheduleModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        event={selectedEvent}
        ownerId={ownerId}
      />
    </Box>
  );
};

export default ScheduleTab;
